/**
 * SavedComparisonsPage - View and manage saved property comparisons
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Scale, Trash2, Eye, Share2, Calendar, Clock,
  MapPin, Bed, Bath, Home, Loader2, ArrowLeft,
  Edit2, Save, X, BookmarkCheck, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SavedComparisonsPage() {
  const navigate = useNavigate();
  const { formatCurrency } = useCountry();
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  
  // Load email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('savedComparisonsEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setEmailSubmitted(true);
    }
  }, []);
  
  // Fetch comparisons when email is submitted
  useEffect(() => {
    if (emailSubmitted && email) {
      fetchComparisons();
    }
  }, [emailSubmitted, email]);
  
  const fetchComparisons = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/compare/saved?user_email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setComparisons(data.comparisons || []);
      } else {
        toast.error('Failed to fetch saved comparisons');
      }
    } catch (error) {
      console.error('Error fetching comparisons:', error);
      toast.error('Failed to load comparisons');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    localStorage.setItem('savedComparisonsEmail', email);
    setEmailSubmitted(true);
  };
  
  const handleDelete = async (comparisonId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/compare/saved/${comparisonId}?user_email=${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setComparisons(prev => prev.filter(c => c.id !== comparisonId));
        toast.success('Comparison deleted');
      } else {
        toast.error('Failed to delete comparison');
      }
    } catch (error) {
      console.error('Error deleting comparison:', error);
      toast.error('Failed to delete comparison');
    }
    setDeleteConfirm(null);
  };
  
  const handleUpdateTitle = async (comparisonId) => {
    if (!editTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    
    const comparison = comparisons.find(c => c.id === comparisonId);
    if (!comparison) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/compare/saved/${comparisonId}?user_email=${encodeURIComponent(email)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            listing_ids: comparison.listing_ids,
            title: editTitle,
            notes: comparison.notes
          })
        }
      );
      
      if (response.ok) {
        setComparisons(prev => prev.map(c => 
          c.id === comparisonId ? { ...c, title: editTitle } : c
        ));
        toast.success('Title updated');
      } else {
        toast.error('Failed to update title');
      }
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error('Failed to update');
    }
    setEditingId(null);
    setEditTitle('');
  };
  
  const handleShare = async (comparison) => {
    try {
      const response = await fetch(`${API_URL}/api/compare/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_ids: comparison.listing_ids,
          title: comparison.title
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}${data.share_url}`;
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Share link copied to clipboard!');
      } else {
        toast.error('Failed to create share link');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share comparison');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const changeEmail = () => {
    localStorage.removeItem('savedComparisonsEmail');
    setEmail('');
    setEmailSubmitted(false);
    setComparisons([]);
  };
  
  // Email entry form
  if (!emailSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8" data-testid="saved-comparisons-page">
        <div className="container mx-auto max-w-md px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <BookmarkCheck className="h-6 w-6 text-blue-600" />
              </div>
              <CardTitle>My Saved Comparisons</CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Enter your email to view your saved property comparisons
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
                <Button 
                  type="submit" 
                  className="w-full"
                  data-testid="view-comparisons-btn"
                >
                  View My Comparisons
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8" data-testid="saved-comparisons-page">
      <div className="container mx-auto max-w-4xl px-3 sm:px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="p-1 sm:p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BookmarkCheck className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                Saved Comparisons
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate max-w-[200px] sm:max-w-none">
                {email} · <button onClick={changeEmail} className="text-blue-600 hover:underline">Change</button>
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchComparisons}
            disabled={loading}
            className="self-end sm:self-auto"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        
        {/* Loading state */}
        {loading && comparisons.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}
        
        {/* Empty state */}
        {!loading && comparisons.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Scale className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No saved comparisons</h3>
              <p className="text-gray-500 mb-4">
                Compare properties and save them for later reference
              </p>
              <Button onClick={() => navigate('/category/property')}>
                Browse Properties
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Comparisons list */}
        <div className="space-y-3 sm:space-y-4">
          {comparisons.map((comparison) => (
            <Card key={comparison.id} className="overflow-hidden" data-testid={`comparison-${comparison.id}`}>
              <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Scale className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                    {editingId === comparison.id ? (
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="h-8 w-32 sm:w-48"
                          autoFocus
                        />
                        <Button size="sm" variant="ghost" onClick={() => handleUpdateTitle(comparison.id)}>
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{comparison.title}</h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 sm:mt-1">
                          <Calendar className="h-3 w-3" />
                          Saved {formatDate(comparison.created_at)}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 sm:gap-2 self-end sm:self-auto">
                    <Badge variant="secondary" className="text-xs">
                      {comparison.listings?.length || 0} props
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => {
                        setEditingId(comparison.id);
                        setEditTitle(comparison.title);
                      }}
                      data-testid={`edit-${comparison.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => handleShare(comparison)}
                      data-testid={`share-${comparison.id}`}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      onClick={() => setDeleteConfirm(comparison.id)}
                      data-testid={`delete-${comparison.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Property preview cards */}
              <CardContent className="p-3 sm:p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                  {comparison.listings?.map((listing, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/listing/${listing.id}`)}
                    >
                      <div className="aspect-video bg-gray-100 relative">
                        {listing.image ? (
                          <img
                            src={listing.image}
                            alt={listing.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Home className="h-8 w-8 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-sm font-medium truncate">{listing.title}</p>
                        <p className="text-blue-600 font-bold text-sm">
                          {formatCurrency(listing.price)}/mo
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          {listing.bedrooms !== undefined && (
                            <span className="flex items-center gap-1">
                              <Bed className="h-3 w-3" />
                              {listing.bedrooms === 0 ? 'Studio' : listing.bedrooms}
                            </span>
                          )}
                          {listing.location && (
                            <span className="flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3" />
                              {listing.location.split(',')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* View comparison button */}
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => handleShare(comparison)}
                    className="gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View Full Comparison
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Delete confirmation dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Delete Comparison
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this saved comparison? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
